/*
 * Copyright (c) 2023 Bolmate B.V.
 */
import './style/sidebarMenu.scss';

import React, {MouseEventHandler, ReactElement, ReactNode, useContext, useEffect, useRef} from 'react';
import classNames from 'classnames';
import {IconName} from '@fortawesome/fontawesome-svg-core';
import {AwesomeIcon} from '@dvrd/dvr-controls';
import {ControlContext} from '@dvrd/dvr-controls/src/js/util/controlContext';
import {BolmateSidebarItem} from "../../util/interfaces";

interface Props {
    onClickItem: (item: BolmateSidebarItem) => MouseEventHandler;
    items: BolmateSidebarItem[];
    numOpenRequests: number;
    activeItems: Array<string>;
}

export default function SidebarMenu(props: Props) {
    const context = useContext(ControlContext);
    const {items, onClickItem, numOpenRequests, activeItems} = props;
    const sideBar = useRef<HTMLDivElement>(null);

    function renderItem(isChild: boolean = false) {
        return function(item: BolmateSidebarItem) {
            const {className, icon, id, children, label} = item;

            const isActive = activeItems.includes(item.id);
            const isOpen = item.alwaysOpen || isActive;
            const cls = classNames(className, 'side-bar-item', item.disabled && 'disabled',
                isChild && 'child', children !== undefined && 'with-children');
            return (
                <div key={id} className={cls} onClick={onClickItem(item)}>
                    {renderIcon(isActive, isChild, label, icon as ReactElement<any>)}
                    {renderItemLabel(isActive, isChild, item)}
                    {renderItemLabel(isActive, isChild, item, true)}
                    {children !== undefined && (
                        <>
                            {isOpen && <div className={classNames('line', isActive && 'active')}/>}
                            {renderChildren(isOpen)(children)}
                        </>
                    )}
                </div>
            );
        }
    }

    function renderItemLabel(isActive: boolean, isChild: boolean, item: BolmateSidebarItem,
                             invisible: boolean = false) {
        const {label, subText} = item;
        let subLabel: string | null = null;
        if (item.disabled) subLabel = '(coming soon)';
        else if (item.id === 'invoice-requests')
            subLabel = `(${numOpenRequests === -1 ? '-' : numOpenRequests > 100 ? '100+' : numOpenRequests})`;
        return (
            <label
                className={classNames('item-label', isActive && 'active',
                    isChild && 'child', invisible && 'invisible')}>{label}{!!subLabel &&
                <span className='item-sub-label'>&nbsp;{subLabel}</span>}
                {!!subText && <span className='sub-label'>{subText}</span>}
            </label>
        )
    }

    function renderChildren(isActive: boolean) {
        return function(children: BolmateSidebarItem[]) {
            const display = isActive ? 'block' : 'none';
            return (
                <div className='children-container' style={{display}}>
                    {children.map(renderItem(true))}
                </div>
            )
        }
    }

    function renderIcon(isActive: boolean, isChild: boolean, label: string,
                        icon?: IconName | string | ReactElement<any>): ReactNode {
        const className = classNames('item-icon', isActive && 'active');
        if (icon) {
            if (typeof icon === 'string') return <AwesomeIcon name={icon as IconName} className={className}/>;
            else if (React.isValidElement<any>(icon))
                return React.cloneElement(icon, {className: classNames(className, icon.props.className)});
        }
        if (label.length) {
            if (isChild) return <span/>;
            return <span className={classNames(className, 'letter')}>{label[0]}</span>
        }
        return null;
    }

    function setColors() {
        const bar = sideBar.current;
        if (bar) {
            bar.style.setProperty('--base-color', context.baseColor);
            bar.style.setProperty('--contrast-color', context.contrastColor);
        }
    }

    useEffect(() => {
        setColors();
    }, []);

    return (
        <div className='bolmate-side-bar-menu full' ref={sideBar} id='bolmate-menu'>
            <div style={{paddingBottom: '5rem'}}>
                {items.map(renderItem())}
            </div>
        </div>
    )
}